import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
    BigModal,
    FlexDiv,
    TextLead,
    TextDefault,
    TextTiny,
    ThemedButton,
    Tag,
    DarkArrowTooltipIcon,
    palette,
} from '@awarego-ui/components';
import Box from '@mui/material/Box';
import EditUserTagsModal from '../single-list-of-users/EditUserTagsModal';
import { getUserListTags } from '../users-helpers';
import { useStores } from '../../../hooks/use-stores';

function EditUserListsTagsModal({ isOpen, onRequestClose, user, companyId }) {
    const { userStore } = useStores();
    const [selectedList, setSelectedList] = useState(null);
    const [editTagsModalOpen, setEditTagsModalOpen] = useState(false);
    const [tagsUser, setTagsUser] = useState(null);

    const lists = user?.lists || [];

    const handleCloseOverview = () => {
        setEditTagsModalOpen(false);
        setSelectedList(null);
        setTagsUser(null);
        onRequestClose();
    };

    const onEditListTags = (_e, list) => {
        setSelectedList(list);
        setTagsUser({
            name: user?.name,
            email: user?.email,
            tags: getUserListTags(list),
        });
        setEditTagsModalOpen(true);
    };

    const handleCloseTagsModal = () => {
        setEditTagsModalOpen(false);
        setSelectedList(null);
        setTagsUser(null);
    };

    const handleSaveTags = async (updatedTags) => {
        if (!selectedList || !user) return;
        try {
            await userStore.updateUserTags(
                companyId,
                selectedList.id,
                user.email,
                updatedTags
            );
            handleCloseTagsModal();
        } catch (e) {
            // Error handled in store via commonStore
        }
    };

    return (
        <>
            <BigModal
                isOpen={isOpen}
                onRequestClose={handleCloseOverview}
                heading={
                    user
                        ? `Edit tags for ${user.name || user.email}`
                        : 'Edit tags'
                }
                extraHeaderContent={
                    <TextLead lighter mt={4}>
                        Tags are stored per employee list. Use the pencil to
                        edit tags for a specific list.
                    </TextLead>
                }
                modalContent={
                    lists.length === 0 ? (
                        <TextDefault lighter>
                            This employee is not in any lists.
                        </TextDefault>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'minmax(160px, 1.2fr) minmax(200px, 2fr) 40px',
                                columnGap: 2,
                                rowGap: 1.5,
                                alignItems: 'center',
                            }}
                        >
                            <TextTiny bold lighter>
                                Group
                            </TextTiny>
                            <TextTiny bold lighter>
                                Tags
                            </TextTiny>
                            <span />
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    mb: 0.5,
                                }}
                            />
                            {lists.map((list) => {
                                const tags = getUserListTags(list);
                                return (
                                    <React.Fragment key={list.id}>
                                        <TextDefault>
                                            {list.name || 'Unnamed list'}
                                        </TextDefault>
                                        <FlexDiv gap={8} alignCenter>
                                            {tags.length === 0 ? (
                                                <TextTiny
                                                    italic
                                                    style={{
                                                        color: palette.graphite
                                                            .ash,
                                                    }}
                                                >
                                                    no tags
                                                </TextTiny>
                                            ) : (
                                                tags.map((tag, i) => (
                                                    <Tag
                                                        key={`${tag}-${i}`}
                                                        text={tag}
                                                    />
                                                ))
                                            )}
                                        </FlexDiv>
                                        <DarkArrowTooltipIcon
                                            row={list}
                                            customFunction={onEditListTags}
                                            title="Edit tags"
                                            icon="edit"
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    )
                }
                modalFooterContent={
                    <ThemedButton
                        mode="secondary"
                        text="Close"
                        onClick={handleCloseOverview}
                    />
                }
            />
            <EditUserTagsModal
                isOpen={editTagsModalOpen}
                onRequestClose={handleCloseTagsModal}
                user={tagsUser}
                listName={selectedList?.name}
                saving={userStore.savingUserTags}
                onSave={handleSaveTags}
                overlayZIndex={1100}
            />
        </>
    );
}

export default observer(EditUserListsTagsModal);
